import './PageListEditor.scss';
import * as React from 'react';
import { INewPage, IPage, PageStatus } from 'src/Entities/Pages';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import Icon from 'src/components/Icon';
import { ChildPage, ParentPage } from './PageListEditorPage';

interface IPageListEditorProps {
  pages: IPage[];
  activePageId: number | null;
  onOrderChange: (orderedPages: IPage[]) => void;
  onSelectPage: (page: IPage) => void;
  onCreateSubPage: (page: INewPage) => void;
}

interface IPageListEditorNode {
  id: number;
  page: IPage;
  children: IPageListEditorNode[];
}

interface IPageNodeMap {
  [key: string]: IPageListEditorNode;
}

function remove<T>(array: T[], indexToRemove: number): T[] {
  return array.filter((item, index) => index !== indexToRemove);
}

function add<T>(array: T[], item: any, index: number): T[] {
  const nextArray = [...array];
  nextArray.splice(index, 0, item);
  return nextArray;
}

const sortByPageOrder = (left: IPage, right: IPage): number => {
  if (left.pageOrder > right.pageOrder) {
    return 1;
  } else if (left.pageOrder < right.pageOrder) {
    return -1;
  }

  return 0;
};

const PageListEditor = ({
  pages,
  activePageId,
  onOrderChange,
  onSelectPage,
  onCreateSubPage
}: IPageListEditorProps) => {
  const tree: {
    nodes: IPageListEditorNode[];
    map: IPageNodeMap;
  } = React.useMemo(() => {
    const pageTree: IPageListEditorNode[] = [];
    const pageNodeMap: IPageNodeMap = {};

    // Create page node and push it to the "flattened" map.
    pages.forEach(page => {
      const node = { id: page.portalPageId, page, children: [] };
      pageNodeMap[page.portalPageId] = node;
    });

    // Build tree from the flattened map.
    pages.sort(sortByPageOrder).forEach(page => {
      const node = pageNodeMap[page.portalPageId];
      if (!node) {
        return;
      }

      if (page.parentPageId !== null) {
        const parentPageNode = pageNodeMap[page.parentPageId];
        parentPageNode.children.push(node);
        return;
      }

      pageTree.push(node);
    });

    return { nodes: pageTree, map: pageNodeMap };
  }, [pages]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const sourceParent = tree.map[result.source.droppableId];
    const targetParent = tree.map[result.destination.droppableId];
    const pageNode = tree.map[result.draggableId];

    if (!sourceParent || !targetParent || !pageNode) {
      return;
    }

    const nextTreeNodes: IPageListEditorNode[] = [...tree.nodes];
    const sourceParentIndex: number = nextTreeNodes.indexOf(sourceParent);
    const targetParentIndex: number = nextTreeNodes.indexOf(targetParent);

    if (sourceParent === targetParent) {
      const nextChildren = [...sourceParent.children];
      // Remove page from current index
      nextChildren.splice(result.source.index, 1);

      // Add page to new index
      nextChildren.splice(result.destination.index, 0, pageNode);

      // Update the parent's children
      nextTreeNodes[sourceParentIndex] = {
        ...sourceParent,
        children: nextChildren
      };
    }

    if (sourceParent !== targetParent) {
      // Remove page from source
      nextTreeNodes[sourceParentIndex] = {
        ...sourceParent,
        children: remove(sourceParent.children, result.source.index)
      };

      // Add page to target
      nextTreeNodes[targetParentIndex] = {
        ...targetParent,
        children: add(targetParent.children, pageNode, result.destination.index)
      };
    }

    const orderedPages: IPage[] = [];
    let nextPageOrder = -1;

    nextTreeNodes.map(parentNode => {
      orderedPages.push({
        ...parentNode.page,
        pageOrder: ++nextPageOrder
      });

      parentNode.children.forEach(childNode => {
        orderedPages.push({
          ...childNode.page,
          parentPageId: parentNode.page.portalPageId,
          pageOrder: ++nextPageOrder
        });
      });
    });

    onOrderChange(orderedPages);
  };

  const handleCreateSubPageClick = (parentNode: IPageListEditorNode) => {
    const lastChildNode = parentNode.children[parentNode.children.length - 1];
    const pageOrder = lastChildNode ? lastChildNode.page.pageOrder + 1 : 1;

    onCreateSubPage({
      title: '',
      slug: '',
      content: '',
      status: PageStatus.DRAFTED,
      parentPageId: parentNode.id,
      pageOrder,
    });
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      {tree.nodes.map(parentNode => (
        <div key={parentNode.id} className="mb-6">
          <ParentPage
            page={parentNode.page}
            isActive={activePageId === parentNode.id}
            onClick={onSelectPage}
          />
          <Droppable droppableId={`${parentNode.id}`}>
            {provided => (
              <div ref={provided.innerRef}>
                {parentNode.children.map((childNode, index) => (
                  <Draggable
                    key={childNode.id}
                    draggableId={`${childNode.id}`}
                    index={index}
                  >
                    {(draggableProvided, snapshot) => (
                      <ChildPage
                        ref={draggableProvided.innerRef}
                        page={childNode.page}
                        isActive={activePageId === childNode.id}
                        handleProps={draggableProvided.dragHandleProps}
                        onClick={onSelectPage}
                        {...draggableProvided.draggableProps}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                <div
                  onClick={() => handleCreateSubPageClick(parentNode)}
                  className="PageListEditorCreateItem flex items-center p-4 font-bold has-text-gray-800 mt-2 ml-10 cursor-pointer"
                >
                  <Icon name="plus" className="w-5 h-5 mr-3" />
                  Tilføj underside
                </div>
              </div>
            )}
          </Droppable>
        </div>
      ))}
    </DragDropContext>
  );
};

export default PageListEditor;



// WEBPACK FOOTER //
// ./src/Pages/PageEditor/components/PageListEditor.tsx